import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { uploadProgressStore } from '../store/UploadProgressStore'
import { UploadItem, UploadProgressContextShape } from '../types'

const defaultContext: UploadProgressContextShape = {
  uploads: new Map(),
  version: 0,
  registerUpload: () => {},
  updateUploadProgress: () => {},
  completeUpload: () => {},
  failUpload: () => {},
  cancelUpload: () => {},
  retryUpload: async () => {},
  removeUpload: () => {},
  addFiles: async () => {},
  getTargetEndpoint: () => null,
  getFormDataParams: () => new Map(),
  setFormDataParams: () => {},
  startShrinkAnimation: () => {},
  setOnSuccess: () => {},
  clearAllUploads: () => {}
}

export const UploadProgressContext = createContext<UploadProgressContextShape>(defaultContext)

export const UploadProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [uploads, setUploads] = useState<Map<string, UploadItem>>(new Map())
  const [version, setVersion] = useState(0)
  const storeRef = useRef(uploadProgressStore)

  useEffect(() => {
    const unsubscribe = storeRef.current.subscribe(() => {
      const newUploads = storeRef.current.getUploads()
      setUploads(new Map(newUploads))
      setVersion(storeRef.current.getVersion())
    })

    return unsubscribe
  }, [])

  const registerUpload = useCallback((upload: UploadItem) => {
    storeRef.current.registerUpload(upload)
  }, [])

  const updateUploadProgress = useCallback((id: string, progress: number) => {
    storeRef.current.updateUploadProgress(id, progress)
  }, [])

  const completeUpload = useCallback((id: string, uploadedTo?: string) => {
    storeRef.current.completeUpload(id, uploadedTo)
  }, [])

  const failUpload = useCallback((id: string, error: string) => {
    storeRef.current.failUpload(id, error)
  }, [])

  const cancelUpload = useCallback((id: string) => {
    storeRef.current.cancelUpload(id)
  }, [])

  const retryUpload = useCallback(async (id: string) => {
    await storeRef.current.retryUpload(id)
  }, [])

  const removeUpload = useCallback((id: string) => {
    storeRef.current.removeUpload(id)
  }, [])

  const addFiles = useCallback(async (files: File[]) => {
    await storeRef.current.addFiles(files)
  }, [])

  const getTargetEndpoint = useCallback(() => {
    return storeRef.current.getTargetEndpoint()
  }, [])

  const getFormDataParams = useCallback(() => {
    return storeRef.current.getFormDataParams()
  }, [])

  const setFormDataParams = useCallback((params: Map<string, any>) => {
    storeRef.current.setFormDataParams(params)
  }, [])

  const startShrinkAnimation = useCallback((id: string) => {
    storeRef.current.startShrinkAnimation(id)
  }, [])

  const setOnSuccess = useCallback((callback: (uploadItem: UploadItem) => void) => {
    storeRef.current.setOnSuccess(callback)
  }, [])

  const clearAllUploads = useCallback(() => {
    storeRef.current.clearAllUploads()
  }, [])

  const contextValue = useMemo<UploadProgressContextShape>(
    () => ({
      uploads,
      version,
      registerUpload,
      updateUploadProgress,
      completeUpload,
      failUpload,
      cancelUpload,
      retryUpload,
      removeUpload,
      addFiles,
      getTargetEndpoint,
      getFormDataParams,
      setFormDataParams,
      startShrinkAnimation,
      setOnSuccess,
      clearAllUploads
    }),
    [
      uploads,
      version,
      registerUpload,
      updateUploadProgress,
      completeUpload,
      failUpload,
      cancelUpload,
      retryUpload,
      removeUpload,
      addFiles,
      getTargetEndpoint,
      getFormDataParams,
      setFormDataParams,
      startShrinkAnimation,
      setOnSuccess,
      clearAllUploads
    ]
  )

  return <UploadProgressContext.Provider value={contextValue}>{children}</UploadProgressContext.Provider>
}
